import { noop } from 'lodash';
import { memo, type ReactNode, useCallback } from 'react';

import { useCommandState } from '@air/cmdk';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { SearchCommandGroup } from '~/components/SearchCommand/SearchCommandGroup';
import { SearchCommandListFrame } from '~/components/SearchCommand/SearchCommandListFrame';
import { SearchCommandSimilarImageSearch } from '~/components/SearchCommand/SearchCommandSimilarImageSearch';
import { SearchCommandSuggestionListItem } from '~/components/SearchCommand/SearchCommandSuggestionListItem';
import { SmartSearchPromotionDropdown } from '~/components/SearchCommand/SmartSearchPromotionDropdown';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { getHasSeenSmartSearchPromotion } from '~/utils/SearchUtils';

export type SmartSearchCommandListProps = {
  isOpen: boolean;
  listStart: ReactNode | undefined;
  onSelect?: (suggestion: string) => void;
  onSimilarImageSearch: (base64Image: string) => void;
};

export const SmartSearchCommandList = memo(
  ({ isOpen, listStart, onSelect, onSimilarImageSearch }: SmartSearchCommandListProps) => {
    const search = useCommandState((state) => state.search);
    const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
    const { ui2026 } = useTemporaryFeatureFlags();
    const showSimilarImageSearch = similarToImageSearchAvailable && !ui2026 && isOpen;

    const showSmartSearchPromotion = !getHasSeenSmartSearchPromotion() && isOpen;

    const handleSelectSearch = useCallback(() => {
      onSelect?.(search);
    }, [onSelect, search]);

    if (search || showSimilarImageSearch) {
      return (
        <SearchCommandListFrame>
          {ui2026 ? (
            <>
              {search && (
                <SearchCommandGroup>
                  <SearchCommandSuggestionListItem isCurrentQuery onSelect={handleSelectSearch} suggestion={search} />
                </SearchCommandGroup>
              )}
              {listStart}
            </>
          ) : (
            <>
              {listStart}
              {search && (
                <SearchCommandGroup>
                  <SearchCommandSuggestionListItem isCurrentQuery onSelect={handleSelectSearch} suggestion={search} />
                </SearchCommandGroup>
              )}
            </>
          )}
          {showSimilarImageSearch && <SearchCommandSimilarImageSearch onSearch={onSimilarImageSearch} />}
        </SearchCommandListFrame>
      );
    }

    if (showSmartSearchPromotion) {
      // TODO: Follow-up in https://linear.app/air/issue/COR-1149/add-learn-more-mechanism-to-smart-search-promotional-dropdown
      if (ui2026) {
        return (
          <div className="flex max-h-[min(480px,calc(100vh-8rem))] min-h-0 grow flex-col overflow-y-auto ads:border-ads-gray-5 border-gray-5 border-t">
            <SmartSearchPromotionDropdown onLearnMoreClick={noop} />
          </div>
        );
      }

      return <SmartSearchPromotionDropdown onLearnMoreClick={noop} />;
    }

    return null;
  },
);

SmartSearchCommandList.displayName = 'SmartSearchCommandList';
